<template>
  <div id="otif-report-page">
    <div class="vx-row">
      <!-- Filter Card -->
      <div class="vx-col w-full">
        <vx-card
          v-can="'view-otif-missed-data'"
          titleIcon="FilterIcon"
          ref="filterCard"
          title="Advanced Filters"
          class="user-list-filters mb-8"
          :collapse-action="true"
        >
          <!-- Quick Preset Buttons -->
          <div class="vx-row mb-3">
            <div class="vx-col w-full flex items-center flex-wrap">
              <span class="text-xs font-semibold text-muted mr-2">Quick Presets:</span>
              <vs-button
                size="small"
                type="border"
                class="mr-2 mb-1"
                @click="setQuickPreset('this_week')"
              >
                This Week
              </vs-button>
              <vs-button
                size="small"
                type="border"
                class="mr-2 mb-1"
                @click="setQuickPreset('last_week')"
              >
                Last Week
              </vs-button>
              <vs-button
                size="small"
                type="border"
                class="mr-2 mb-1"
                @click="setQuickPreset('this_month')"
              >
                This Month
              </vs-button>
              <vs-button
                size="small"
                type="border"
                class="mr-2 mb-1"
                @click="setQuickPreset('last_month')"
              >
                Last Month
              </vs-button>
            </div>
          </div>

          <div class="vx-row">
            <!-- Date From Picker -->
            <div class="vx-col md:w-1/3 sm:w-1/2 w-full mb-2">
              <label class="text-sm opacity-100 text-bold">Date From</label>
              <flat-pickr
                class="w-full"
                :config="flatPkrConfig"
                v-model="filters.from"
                placeholder="Choose Date From"
                @on-change="onDateChange"
              />
            </div>

            <!-- Date To Picker -->
            <div class="vx-col md:w-1/3 sm:w-1/2 w-full mb-2">
              <label class="text-sm opacity-100 text-bold">Date To</label>
              <flat-pickr
                class="w-full"
                :config="flatPkrConfig"
                v-model="filters.to"
                placeholder="Choose Date To"
                @on-change="onDateChange"
              />
            </div>

            <!-- Region Filter -->
            <div class="vx-col md:w-1/3 sm:w-1/2 w-full mb-2">
              <label class="text-sm opacity-100 text-bold">Region</label>
              <v-select
                v-model="filters.region"
                :options="regionOptions"
                :reduce="opt => opt.value"
                label="label"
                :clearable="true"
                placeholder="All Regions"
                @input="onChangeRegion"
              />
            </div>
          </div>

          <!-- Bottom Actions -->
          <div class="vx-row mt-5 hidden">
            <div class="vx-col w-full ml-auto">
              <vs-button
                class="float-right mr-4"
                size="small"
                color="danger"
                type="border"
                icon-pack="feather"
                @click="onClickClear"
              >
                CLEAR
              </vs-button>
            </div>
          </div>
        </vx-card>

        <!-- Main Report Listing Card -->
        <vx-card title="OTIF MISSED ITEMS REPORT" v-can="'view-otif-missed-data'">
          <template slot="actions">
            <div class="flex items-center flex-wrap">
              <div v-if="filters.from && filters.to" class="mr-3 mb-2">
                <vs-chip color="primary" class="font-medium">
                  Period: {{ filters.from }} - {{ filters.to }}
                </vs-chip>
              </div>
              <div v-else-if="filters.from" class="mr-3 mb-2">
                <vs-chip color="primary" class="font-medium">
                  From: {{ filters.from }}
                </vs-chip>
              </div>
              <div v-else-if="filters.to" class="mr-3 mb-2">
                <vs-chip color="primary" class="font-medium">
                  To: {{ filters.to }}
                </vs-chip>
              </div>
              <div v-else-if="month_year" class="mr-3 mb-2">
                <vs-chip color="primary" class="font-medium">
                  Period: {{ month_year }}
                </vs-chip>
              </div>
              <div v-else class="mr-3 mb-2">
                <vs-chip color="dark" class="font-medium">
                  Period: All
                </vs-chip>
              </div>
              <div v-if="filters.region" class="mr-3 mb-2">
                <vs-chip color="success" class="font-medium" closable @click="clearRegionFilter">
                  Region: {{ filters.region }}
                </vs-chip>
              </div>
              <div class="mr-3 mb-2">
                <vs-chip color="warning" class="font-medium">
                  Total Items: {{ meta.total || 0 }}
                </vs-chip>
              </div>
              <excel-download
                :name="'courier-shipment-otif-sheet'"
                :filters="{ from: filters.from, to: filters.to, month_year: month_year, month: month_year, 'filter[region]': filters.region, sort: sort }"
                button_label="Download OTIF Report"
                class="mb-2"
              />
            </div>
          </template>

          <div slot="no-body" class="mt-4">
            <vs-table
              :max-items="perPage"
              :data="report_items"
              @sort="handleSort"
              :sst="true"
              stripe
              class="table-dark-inverted table-auto vs-con-loading__container"
            >
              <template slot="thead">
                <vs-th sort-key="product_order_code">SO NUMBER</vs-th>
                <vs-th sort-key="formulation_code">PRODUCT CODE</vs-th>
                <vs-th>CUSTOMER</vs-th>
                <vs-th sort-key="region">REGION</vs-th>
                <vs-th sort-key="quantity">ORDER QTY</vs-th>
                <vs-th sort-key="quantity_delivered_in_time">QTY DELIVERED IN TIME</vs-th>
                <vs-th sort-key="required_by">REQUIRED BY</vs-th>
                <vs-th sort-key="shipment_date">SHIPMENT DATE</vs-th>
                <vs-th>OTIF STATUS</vs-th>
                <vs-th style="min-width: 280px">VAM DETAILS / REASONS</vs-th>
                <vs-th>REMARKS</vs-th>
                <vs-th v-can="'update-otif-missed-data-remarks'">ACTION</vs-th>
              </template>

              <template slot-scope="{ data }">
                <vs-tr
                  :key="indextr"
                  v-for="(tr, indextr) in data"
                  class="cursor-pointer"
                  @click.native="onClickRow(tr)"
                >
                  <!-- Sales Order Code -->
                  <vs-td :data="tr.product_order_code">
                    <HyperLink :id="tr.product_order.id" type="product-order">{{ tr.product_order.product_order_code }}</HyperLink>
                  </vs-td>

                  <!-- Product / Formulation Code -->
                  <vs-td :data="tr.formulation_code">
                    <span class="font-medium">
                      {{ tr.formulation_code || '-' }}
                    </span>
                  </vs-td>

                  <!-- Customer -->
                  <vs-td :data="tr.product_order">
                    <HyperLink :id="tr.product_order.customer.id" type="customer">{{ tr.product_order.customer.company_name }}</HyperLink>
                  </vs-td>

                  <!-- Region -->
                  <vs-td :data="tr.region">
                    {{ tr.region || '-' }}
                  </vs-td>

                  <!-- Order Quantity -->
                  <vs-td :data="tr.quantity">
                    <span class="font-semibold">
                      {{ formatQuantity(tr.quantity) }}
                    </span>
                  </vs-td>

                  <!-- Quantity Delivered In Time -->
                  <vs-td :data="tr.quantity_delivered_in_time">
                    <span
                      :class="Number(tr.quantity_delivered_in_time) > 0 ? 'text-success font-semibold' : 'text-danger font-semibold'"
                    >
                      {{ formatQuantity(tr.quantity_delivered_in_time) }}
                    </span>
                  </vs-td>

                  <!-- Required By Date -->
                  <vs-td :data="tr.required_by">
                    <span>{{ tr.required_by || '-' }}</span>
                  </vs-td>

                  <!-- Shipment Date -->
                  <vs-td :data="tr.shipment_date">
                    <span>{{ tr.shipment_date || '-' }}</span>
                  </vs-td>

                  <!-- OTIF Status -->
                  <vs-td :data="tr.is_otif">
                    <vs-chip
                      v-if="tr.is_otif"
                      color="success"
                      class="text-xs font-semibold"
                    >
                      OTIF
                    </vs-chip>
                    <vs-chip
                      v-else
                      color="danger"
                      class="text-xs font-semibold"
                    >
                      Missed
                    </vs-chip>
                  </vs-td>

                  <!-- VAM Data Notes -->
                  <vs-td :data="tr.vam_data">
                    <ul v-if="tr.vam_data && tr.vam_data.length" class="vam-raw-list pl-0 mb-0">
                      <li
                        v-for="(note, idx) in tr.vam_data"
                        :key="idx"
                        class="vam-raw-item flex items-start mb-1.5 text-xs"
                      >
                        <span class="vam-bullet text-primary mr-2 font-bold">•</span>
                        <span class="leading-normal">{{ note }}</span>
                      </li>
                    </ul>
                    <span v-else class="text-muted text-xs">-</span>
                  </vs-td>

                  <!-- Remarks -->
                  <vs-td :data="tr.otif_remarks">
                    <span class="text-xs">
                      {{ tr.otif_remarks || '-' }}
                    </span>
                  </vs-td>

                  <!-- Action Button -->
                  <vs-td v-can="'update-otif-missed-data-remarks'">
                    <div class="flex items-center">
                      <vx-tooltip text="Update Remarks" position="left">
                        <vs-button
                          @click.stop="onClickEditRemarks(tr)"
                          size="small"
                          color="warning"
                          type="border"
                          icon-pack="feather"
                          icon="icon-edit-2"
                        />
                      </vx-tooltip>
                    </div>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>

            <!-- Empty State -->
            <div
              v-if="(!report_items || report_items.length === 0) && !is_loading"
              class="text-center py-10"
            >
              <feather-icon
                icon="InboxIcon"
                svgClasses="h-12 w-12 text-muted mb-2 inline-block"
              />
              <p class="text-base text-muted font-medium">
                No OTIF missed items found for the selected criteria.
              </p>
            </div>

            <!-- Pagination -->
            <vs-pagination
              v-if="meta && meta.last_page > 1"
              @changeMaxItems="changePerPage"
              :size-array="meta.total ? meta.total : 0"
              :max-items="perPage"
              description-body="Rows per page"
              description-connector="of"
              description-title="Records"
              description
              :description-items="descriptionItems"
              :total="meta.last_page ? meta.last_page : 0"
              v-model="page"
              @input="handleChangePage"
              class="p-5"
            />
          </div>
        </vx-card>
      </div>
    </div>

    <!-- Update OTIF Remarks Popup Modal -->
    <vs-popup
      title="UPDATE OTIF REMARKS"
      :active.sync="isRemarksPopupOpen"
      class="otif-remarks-popup"
      v-can="'update-otif-missed-data-remarks'"
    >
      <div v-if="selectedRemarkItem" class="p-1">
        <div class="summary-info-card mb-5">
          <div class="vx-row">
            <div class="vx-col w-1/3">
              <span class="info-label">Sales Order</span>
              <span class="info-val text-primary font-bold">{{ selectedRemarkItem.product_order.product_order_code || '-' }}</span>
            </div>
            <div class="vx-col w-1/3">
              <span class="info-label">Product Code</span>
              <span class="info-val font-semibold">{{ selectedRemarkItem.formulation_code || '-' }}</span>
            </div>
            <div class="vx-col w-1/3">
              <span class="info-label">Required By</span>
              <span class="info-val font-medium">{{ selectedRemarkItem.required_by || '-' }}</span>
            </div>
          </div>
        </div>

        <div class="vx-row mb-4">
          <div class="vx-col w-full">
            <label class="text-sm font-semibold mb-1 block">OTIF Remarks</label>
            <vs-textarea
              v-model="editRemarkText"
              placeholder="Enter remarks for this OTIF missed item..."
              rows="4"
              class="w-full mb-0"
            />
          </div>
        </div>

        <div class="flex items-center justify-end mt-4">
          <vs-button
            color="danger"
            type="border"
            class="mr-2"
            size="small"
            @click="isRemarksPopupOpen = false"
          >
            Cancel
          </vs-button>
          <vs-button
            color="primary"
            type="filled"
            size="small"
            :disabled="isSavingRemarks"
            @click="onSaveRemarks"
          >
            {{ isSavingRemarks ? 'Saving...' : 'Save Remarks' }}
          </vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import moment from 'moment'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import MonthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index.js'
import 'flatpickr/dist/plugins/monthSelect/style.css'
import ExcelDownload from '@/components/excel/ExcelDownload.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import ErrorMessages from '@/mixins/ErrorMessages'
import SuccessMessage from '@/mixins/SuccessMessage'
import { defaultPagination, perPageList } from '@/constants/config'

const ReportingRepository = RepositoryFactory.get('reporting')

export default {
  name: 'OtifReport',
  components: {
    flatPickr,
    ExcelDownload,
    'v-select': vSelect
  },
  mixins: [ErrorMessages, SuccessMessage],
  data() {
    return {
      report_items: [],
      filters: {
        from: moment().subtract(1, 'month').startOf('month').format('DD/MM/YYYY'),
        to: moment().subtract(1, 'month').endOf('month').format('DD/MM/YYYY'),
        region: ''
      },
      regionOptions: [
        { label: 'All Regions', value: '' },
        { label: 'Great Britain', value: 'Great Britain' },
        { label: 'Europe', value: 'Europe' },
        { label: 'ROW', value: 'ROW' }
      ],
      month_year: '',
      sort: '',
      page: 1,
      perPage: 15,
      meta: {
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 15
      },
      is_loading: false,
      descriptionItems: perPageList || [15, 25, 50, 100],
      selectedRemarkItem: null,
      editRemarkText: '',
      isRemarksPopupOpen: false,
      isSavingRemarks: false
    }
  },
  computed: {
    flatPkrConfig() {
      return {
        dateFormat: 'd/m/Y'
      }
    },
    flatPkrMonthYearConfig() {
      return {
        dateFormat: 'm/Y',
        plugins: [
          new MonthSelectPlugin({
            shorthand: true,
            dateFormat: 'm/Y',
            altFormat: 'm/Y'
          })
        ]
      }
    }
  },
  watch: {
    '$route.query': {
      handler(newQuery) {
        if (newQuery && (newQuery.from || newQuery.to || newQuery.month_year || newQuery['filter[region]'])) {
          this.applyRouteFilters()
          this.fetchOtifReport(1)
        }
      },
      deep: true
    }
  },
  async mounted() {
    this.applyRouteFilters()
    await this.fetchOtifReport(1)
  },
  methods: {
    applyRouteFilters() {
      const from = this.$route.query.from
      const to = this.$route.query.to
      if (from) {
        this.filters.from = from
      }
      if (to) {
        this.filters.to = to
      }
      const region = this.$route.query['filter[region]']
      if (region) {
        this.filters.region = region
      }
      const monthYear = this.$route.query.month_year || (this.$route.params && this.$route.params.month_year)
      if (monthYear && !from && !to) {
        this.filters.from = moment(monthYear, 'MM/YYYY').startOf('month').format('DD/MM/YYYY')
        this.filters.to = moment(monthYear, 'MM/YYYY').endOf('month').format('DD/MM/YYYY')
        this.month_year = monthYear
      }
    },
    onChangeRegion() {
      this.page = 1
      this.fetchOtifReport(1)
    },
    clearRegionFilter() {
      this.filters.region = ''
      this.page = 1
      this.fetchOtifReport(1)
    },
    setQuickPreset(key) {
      switch (key) {
        case 'this_week':
          this.filters.from = moment().startOf('isoWeek').format('DD/MM/YYYY')
          this.filters.to = moment().endOf('isoWeek').format('DD/MM/YYYY')
          break
        case 'last_week':
          this.filters.from = moment().subtract(1, 'week').startOf('isoWeek').format('DD/MM/YYYY')
          this.filters.to = moment().subtract(1, 'week').endOf('isoWeek').format('DD/MM/YYYY')
          break
        case 'this_month':
          this.filters.from = moment().startOf('month').format('DD/MM/YYYY')
          this.filters.to = moment().endOf('month').format('DD/MM/YYYY')
          break
        case 'last_month':
          this.filters.from = moment().subtract(1, 'month').startOf('month').format('DD/MM/YYYY')
          this.filters.to = moment().subtract(1, 'month').endOf('month').format('DD/MM/YYYY')
          break
      }
      this.month_year = ''
      this.page = 1
      this.fetchOtifReport(1)
    },
    handleSort(key, active) {
      if (key && active) {
        this.sort = (active === 'desc' ? '-' : '') + key
      } else {
        this.sort = ''
      }
      this.page = 1
      this.fetchOtifReport(1)
    },
    async fetchOtifReport(pageNumber = 1) {
      if (this.is_loading) return
      this.is_loading = true
      this.$vs.loading()
      try {
        const payload = {
          from: this.filters.from,
          to: this.filters.to
        }
        if (this.filters.region) {
          payload['filter[region]'] = this.filters.region
        }
        if (this.sort) {
          payload.sort = this.sort
        }
        if (this.month_year && !this.filters.from && !this.filters.to) {
          payload.month_year = this.month_year
        }
        const response = await ReportingRepository.getOtifMissedItems(
          pageNumber,
          payload,
          this.perPage
        )
        const responseData = response.data || {}
        this.report_items = responseData.data || []
        this.meta = responseData.meta || {
          current_page: pageNumber,
          last_page: 1,
          total: this.report_items.length,
          per_page: this.perPage
        }
        this.page = this.meta.current_page || pageNumber
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.is_loading = false
        this.$vs.loading.close()
      }
    },
    onDateChange(selectedDates, dateStr) {
      this.page = 1
      this.fetchOtifReport(1)
    },
    handleChangePage(page) {
      this.page = page
      this.fetchOtifReport(page)
    },
    onClickRefresh() {
      this.fetchOtifReport(this.page)
    },
    onClickClear() {
      this.filters.from = ''
      this.filters.to = ''
      this.filters.region = ''
      this.month_year = ''
      this.sort = ''
      this.page = 1
      this.fetchOtifReport(1)
    },
    changePerPage(index) {
      this.perPage = this.descriptionItems[index] || 15
      this.page = 1
      this.fetchOtifReport(1)
    },
    formatQuantity(qty) {
      if (qty === null || qty === undefined || qty === '') return '-'
      const num = Number(qty)
      return isNaN(num) ? qty : num.toLocaleString()
    },
    onClickRow(item) {
      if (item && item.product_order_id) {
        this.$router.push({ path: `/pages/product-orders/${item.product_order_id}` }).catch(() => {})
      }
    },
    onClickEditRemarks(item) {
      this.selectedRemarkItem = item
      this.editRemarkText = item.otif_remarks || ''
      this.isRemarksPopupOpen = true
    },
    async onSaveRemarks() {
      if (!this.selectedRemarkItem || !this.selectedRemarkItem.id) return
      this.isSavingRemarks = true
      this.$vs.loading()
      try {
        await ReportingRepository.updateOtifRemarks(this.selectedRemarkItem.id, {
          otif_remarks: this.editRemarkText
        })
        this.selectedRemarkItem.otif_remarks = this.editRemarkText
        this.$vs.notify({
          title: 'Success',
          text: 'OTIF remarks updated successfully',
          color: 'success',
          iconPack: 'feather',
          icon: 'icon-check'
        })
        this.isRemarksPopupOpen = false
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.isSavingRemarks = false
        this.$vs.loading.close()
      }
    }
  }
}
</script>

<style lang="scss">
.vam-raw-list {
  list-style: none;
  max-width: 460px;
}
.vam-raw-item {
  line-height: 1.45;
}
.vam-bullet {
  font-size: 0.95rem;
  line-height: 1;
  color: #7367f0;
}
.summary-info-card {
  background-color: #f8f8f8;
  border-radius: 8px;
  padding: 12px 14px;
}
.info-label {
  display: block;
  font-size: 0.72rem;
  color: #82868b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}
.info-val {
  display: block;
  font-size: 0.88rem;
  word-break: break-word;
}

/* Dark theme support */
.theme-dark {
  .summary-info-card {
    background-color: #10163a;
    border: 1px solid rgba(255, 255, 255, 0.08);
  }
  .info-label {
    color: #9aa2ba;
  }
  .info-val {
    color: #c2c6dc;
  }
}
</style>
